import React from 'react';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import StarIcon from '@mui/icons-material/Star';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import TextFieldsIcon from '@mui/icons-material/TextFields';

// Import the new visualization components
import TextProficiency from './definitions/TextProficiency';
import StarsProficiency from './definitions/StarsProficiency';
import BarsProficiency from './definitions/BarsProficiency';
import CirclesProficiency from './definitions/CirclesProficiency';
import SquaresProficiency from './definitions/SquaresProficiency';

/*
  Bar Style Registry
  ------------------
  A bar style controls how skill/language proficiency is visualised.
  Each definition points to a React component responsible for rendering.
*/

// "None" – renders as plain text (via TextProficiency with specific handling for "none")
// OR, we can make "None" truly render nothing if TextProficiency handles null/empty proficiency.
// For now, let's assume TextProficiency can be used, or we make a dedicated NoneProficiency component if needed.
// Let's use TextProficiency for the "none" case as it will effectively show nothing if no proficiency string is passed,
// or just the text if that's what we want.
// Re-evaluating: For a true "None" style, the component should be null or a component that renders null.
// The TextProficiency component is for rendering the proficiency string as text, e.g., "(Intermediate)".
// Let's make "None" use a null component, and add a separate "Text" style.

export const NoneBarStyle = {
  id: 'none',
  name: 'None',
  description: 'No visual proficiency indicator.',
  component: null, // No component, layouts will render nothing for proficiency vis
  icon: <DoNotDisturbOnOutlinedIcon />,
  // settings: { visualization: 'none' } // No longer needed, component itself defines visualization
};

export const TextBarStyle = {
  id: 'text',
  name: 'Textual',
  description: 'Show proficiency level as text (e.g., Intermediate).',
  component: TextProficiency,
  icon: <TextFieldsIcon />,
};

export const StarsBarStyle = {
  id: 'stars',
  name: 'Stars',
  description: 'Render proficiency as a row of stars (1-5).',
  component: StarsProficiency,
  icon: <StarIcon />,
  // settings: { visualization: 'stars' } // No longer needed
};

export const BarsBarStyle = {
  id: 'bars',
  name: 'Bars',
  description: 'Render proficiency as horizontal bars (1-5).',
  component: BarsProficiency,
  icon: <EqualizerIcon />,
  // settings: { visualization: 'bars' } // No longer needed
};

export const CirclesBarStyle = {
  id: 'circles',
  name: 'Circles',
  description: 'Render proficiency as a row of circles (1-5).',
  component: CirclesProficiency,
  icon: <RadioButtonCheckedIcon />,
};

export const SquaresBarStyle = {
  id: 'squares',
  name: 'Squares',
  description: 'Render proficiency as a row of squares (1-5).',
  component: SquaresProficiency,
  icon: <CropSquareIcon />,
};

// Registry map
export const BAR_STYLES_REGISTRY = {
  [NoneBarStyle.id]: NoneBarStyle,
  [TextBarStyle.id]: TextBarStyle,
  [StarsBarStyle.id]: StarsBarStyle,
  [BarsBarStyle.id]: BarsBarStyle,
  [CirclesBarStyle.id]: CirclesBarStyle,
  [SquaresBarStyle.id]: SquaresBarStyle,
};

// Default
export const DEFAULT_BAR_STYLE_ID = 'stars';

// Helper getters
export const getAllBarStyles = () => Object.values(BAR_STYLES_REGISTRY);

export const getBarStyleById = (id) =>
  BAR_STYLES_REGISTRY[id] || BAR_STYLES_REGISTRY[DEFAULT_BAR_STYLE_ID];

// Helper function to get translated bar style names
export const getTranslatedBarStyleName = (barStyleId, t) => {
  return t(`barStyles.${barStyleId}`) || BAR_STYLES_REGISTRY[barStyleId]?.name;
};

// Helper function to get translated bar style description
export const getTranslatedBarStyleDescription = (barStyleId, t) => {
  return t(`barStyles.descriptions.${barStyleId}`) || BAR_STYLES_REGISTRY[barStyleId]?.description;
}; 